import Table from '@amzn/meridian/table/table';
import TableCell from '@amzn/meridian/table/table-cell';
import TableRow from '@amzn/meridian/table/table-row';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import TmTableRowContent, { TmTableRowContentProps } from './TmTableRowContent';
import Pagination from '@amzn/meridian/pagination/pagination';
import DataState from '../../Types/DataState';
import Column from '@amzn/meridian/column/column';
import Row from '@amzn/meridian/row/row';
import Loader from '@amzn/meridian/loader/loader';
import Box from '@amzn/meridian/box';
import Alert from '@amzn/meridian/alert/alert';
import { colorBlue50, colorGray0 } from '@amzn/meridian-tokens/base/color';
import { TMCoachingStatsSummaryForOM } from '../../Types/CoachingStats';
import { SortDirection } from '../../Types/SortDirection';
import { TmTableSortColumn } from './types';
import { tmTableCustomSort } from './OmCoachingHelper';

interface TmTableProps {
  tmTableRowContentPropsList: TMCoachingStatsSummaryForOM[];
  batchCoachingStatsState: string;
  selectedTmCoachingStatsSummary: TMCoachingStatsSummaryForOM;
  setSelectedTmUserId: Function;
}

const TmTable: React.FC<TmTableProps> = ({
  tmTableRowContentPropsList,
  batchCoachingStatsState,
  selectedTmCoachingStatsSummary,
  setSelectedTmUserId,
}): React.ReactElement => {
  const [currentPage, setCurrentPage] = useState(1);
  const TMS_PER_PAGE = 8;
  const numberOfPages = Math.ceil(
    tmTableRowContentPropsList.length / TMS_PER_PAGE
  );
  const firstVisibleIndex = (currentPage - 1) * TMS_PER_PAGE;
  const lastVisibleIndex = firstVisibleIndex + TMS_PER_PAGE;
  const [sortColumn, setSortColumn] = useState(
    TmTableSortColumn.ASSOCIATE_NAME
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.ASCENDING
  );
  const onSort = ({ sortColumn, sortDirection }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  tmTableRowContentPropsList.sort((a, b) => {
    return tmTableCustomSort(a, b, sortColumn, sortDirection);
  });

  return (
    <Column width='100%'>
      <Row height='720px' alignmentVertical='top'>
        <Table
          headerRows={1}
          showDividers
          spacing='small'
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          <TableRow>
            <TableCell sortColumn={TmTableSortColumn.ASSOCIATE_NAME}>
              <FormattedMessage
                id='cs_perfportal_coaching_page_tm_table_header_tm'
                defaultMessage='TM'
              />
            </TableCell>
            <TableCell sortColumn={TmTableSortColumn.STATUS}>
              <FormattedMessage
                id='cs_perfportal_coaching_page_tm_table_header_status'
                defaultMessage='Status'
              />
            </TableCell>
            <TableCell
              sortColumn={TmTableSortColumn.WEEKLY_COACHINGS_COMPLETED}
            >
              <FormattedMessage
                id='cs_perfportal_coaching_page_tm_table_header_weekly'
                defaultMessage='Weekly'
              />
            </TableCell>
            <TableCell
              sortColumn={TmTableSortColumn.MONTHLY_COACHINGS_COMPLETED}
            >
              <FormattedMessage
                id='cs_perfportal_coaching_page_tm_table_header_monthly'
                defaultMessage='Monthly'
              />
            </TableCell>
          </TableRow>
          {batchCoachingStatsState === DataState.LOADING && (
            <Row
              type='outline'
              width='100%'
              alignmentHorizontal='center'
              height='600px'
            >
              <Loader />
            </Row>
          )}
          {batchCoachingStatsState === DataState.ERROR && (
            <Box height='600px' width='fill' spacingInset='300'>
              <Alert type='error'>
                <FormattedMessage
                  id='cs_perfportal_error_refresh_page'
                  defaultMessage='Something went wrong. Please try refreshing the page. 
                If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                />
              </Alert>
            </Box>
          )}
          {batchCoachingStatsState === DataState.LOADED &&
            tmTableRowContentPropsList
              .slice(firstVisibleIndex, lastVisibleIndex)
              .map(tm => (
                <TableRow
                  key={tm.tmInfo.userId}
                  onClick={() => {
                    setSelectedTmUserId(tm.tmInfo.userId);
                  }}
                  backgroundColor={
                    selectedTmCoachingStatsSummary.tmInfo.userId ===
                    tm.tmInfo.userId
                      ? colorBlue50
                      : colorGray0 // TODO: consult with David to decide whether we need to update this to just showing the border instead.
                  }
                >
                  <TmTableRowContent tmCoachingStatsSummary={tm} />
                </TableRow>
              ))}
        </Table>
      </Row>
      <Row alignmentVertical='bottom' alignmentHorizontal='center'>
        <Pagination
          showSkipArrows
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Row>
    </Column>
  );
};

export default TmTable;
